import { useState, useEffect } from "react";
import { ResponsiveContainer } from "recharts";
import { Link } from "react-router-dom";
import { PieCharts } from "./PieChart";
import { AreaCharts } from "./AreaChart";
import { StateDistributionChart } from "./StateDistributionChart";
import { AdminDashboard } from "Services/API/Dashboard.service";
import {
  formattedDailyVisits,
  formattedPatientByState,
  formattedPatientMemberships,
  formattedProviderByState,
} from "Helpers/Dashboard";
import { errorFormatter } from "Helpers/ErrorMsgHandler";
import { ReactComponent as DotIcon } from "Assets/Dot.svg";
import { ReactComponent as ActiveUserIcon } from "Assets/Active Users.svg";
import { ReactComponent as TotalUserIcon } from "Assets/Total Users.svg";
import { ReactComponent as SadFaceIcon } from "Assets/SadFace.svg";
import { Card } from "./Card";
import "./Dashboard.css";
import "Colors.css";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { DateRangePicker } from "Components/Common/DateRangePicker/DateRangePicker";

// Local YYYY-MM-DD, optionally N days back.
const ymd = (daysAgo = 0) => {
  const d = new Date();
  if (daysAgo) d.setDate(d.getDate() - daysAgo);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// 6 days back through today = 7 days inclusive, matching the picker's
// "Last 7 days" preset exactly so the default and the preset agree.
const DEFAULT_DAYS_BACK = 6;

export function Dashboard() {
  // Defaults to the last 7 days: recent enough to reflect what is happening
  // now, and cheap — every login pays for this query, so the default is the
  // one range nobody should have to wait for. Longer ranges and All time are
  // a click away in the picker.
  const [from, setFrom] = useState(() => ymd(DEFAULT_DAYS_BACK));
  const [to, setTo] = useState(() => ymd());
  const [dailyVisits, setDailyVisits] = useState([]);
  const [totalUsers, setTotalUsers] = useState("");
  const [activeUsers, setActiveUsers] = useState("");
  const [patientMemberships, setPatientMeberships] = useState([]);
  const [patientPerState, setPatientPerState] = useState([]);
  const [providerPerState, setProviderPerState] = useState([]);
  // Distinct enabled providers. The per-state counts overlap (one provider is
  // licensed in several states), so the chart can't derive this by summing.
  // Starts undefined, and stays undefined if the backend doesn't send the
  // field: the chart then falls back to summing, which is wrong but sane,
  // rather than confidently reporting a total of 0 providers.
  const [providerTotal, setProviderTotal] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    setIsLoading(true);
    AdminDashboard({ from, to })
      .then((res) => {
        setTotalUsers(res?.totalUsers);
        setActiveUsers(res?.activeUsers);
        setPatientMeberships(
          formattedPatientMemberships(res?.patientMembershipCounts)
        );
        setDailyVisits(formattedDailyVisits(res?.visitCounts));
        setProviderPerState(
          formattedProviderByState(res?.providerCountByStates)
        );
        setProviderTotal(
          typeof res?.totalProvidersInStates === "number"
            ? res.totalProvidersInStates
            : undefined
        );
        setPatientPerState(formattedPatientByState(res?.patientsCountByStates));
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("[DASHBOARD][API_ERROR] - ", err);
        setApiError(
          errorFormatter(err?.response?.data?.errors || err?.message)
        );
        setIsLoading(false);
      });
  }, [from, to]);

  return (
    <>
      {apiError && (
        <>
          <div className="flex flex-col items-center justify-center h-full">
            <SadFaceIcon className="h-16 w-16  animate-bounce text-red-800" />
            <p className="mt-1 text-md font-medium animate-pulse text-red-800">
              {apiError}
            </p>
          </div>
        </>
      )}
      {isLoading && <Spinner />}
      {!isLoading && !apiError && (
        <div className="flex flex-col gap-6 p-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <span className="font-semibold text-2xl sapphire">
              Dashboard
            </span>
            {/* Same control as the Conversions page. Time-series tiles follow
                it; snapshot figures (total/active users, per-state, lobby) are
                current-state counts and stay as they are. */}
            <DateRangePicker
              from={from}
              to={to}
              onChange={({ from: f, to: t }) => { setFrom(f); setTo(t); }}
            />
          </div>
          <div className="flex flex-wrap gap-4 justify-between">
            <div className="Daily_Visits flex flex-1 flex-col gap-6 rounded-md shadow-lg">
              <span className="text-sm leading-6 font-semibold sapphire pl-6 pt-3">
                Daily Visits
              </span>
              <ResponsiveContainer width="100%" height="100%">
                <AreaCharts
                  data={[
                    ...dailyVisits.sort(
                      (a, b) =>
                        new Date(...a.date.split("/").reverse()) -
                        new Date(...b.date.split("/").reverse())
                    ),
                  ]}
                />
              </ResponsiveContainer>
            </div>
            <div className="Total_Users flex flex-1 flex-col gap-2">
              <Card
                heading="Total Users"
                icon={<TotalUserIcon />}
                count={totalUsers}
              />
              <Card
                heading="Active Users"
                icon={<ActiveUserIcon />}
                count={activeUsers}
              />
              <div className="flex flex-col rounded-lg shadow-lg">
                <span className="text-sm leading-6 font-semibold sapphire pl-3 pt-3">
                  Patient Memberships
                </span>
                <div className="flex bg-white shadow-lg">
                  <PieCharts
                    data={patientMemberships}
                    width={200}
                    height={200}
                    innerRadius={60}
                    outerRadius={80}
                  />
                  <div className="flex flex-col gap-4 pt-12">
                    {" "}
                    {patientMemberships.map((membership, index) => (
                      <span className="flex justify-between gap-4" key={index}>
                        <span className="flex items-center gap-2">
                          {index === 0 ? (
                            <span className="text-zircon">{<DotIcon />}</span>
                          ) : index === 1 ? (
                            <span className="text-black">{<DotIcon />}</span>
                          ) : (
                            <span className="text-blue-chill">
                              {<DotIcon />}
                            </span>
                          )}
                          {membership.name}
                        </span>
                        <span className="pr-2"> {membership.value}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* */}
          <div className="flex flex-wrap gap-4 justify-between">
            <div className="User_Per_State flex flex-col rounded-lg shadow-lg">
              <span className="text-sm leading-6 font-semibold sapphire pl-3 pt-3 pb-2">
                Enabled providers in each state
              </span>
              <StateDistributionChart
                data={providerPerState}
                unit="providers"
                total={providerTotal}
              />
            </div>
            <div className="User_Per_State flex flex-col rounded-lg shadow-lg">
              <span className="text-sm leading-6 font-semibold sapphire pl-3 pt-3 pb-2">
                Patients in each state
              </span>
              <StateDistributionChart data={patientPerState} unit="patients" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
